import React, { createRef, useMemo, useCallback } from "react";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { isSpectator, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { CircleIcon, PrimaryButton, SquareIcon, TopBar } from "@kalyzee/kalyzee-ui";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { ToolboxMenu } from "./ToolboxMenu";
import { LayoutSelector } from "./LayoutSelector";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { diagLogger } from "../../helpers/mediaDiagnostics";
import { toastError } from "../../helpers/toast";
import OverlayInformationMessage from "../utils/OverlayInformationMessage";
import { MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isExpert } from "../../helpers/roles";
import { HighlightButton } from "../highlight/button";
import { AppSoundSlider } from "../utils/AppSoundSlider";
import { MediaSourceViewer } from "./MediaSourceViewer";
import { MeetingSourceExtended } from "../../hooks/meeting";
import { ScreenShareButton } from "../screenShare/button";
import { ScreenShareState } from "../../hooks/screenShare";
import { getTogglableMedias, getTogglableMediasSelector, togglableMediasEnabled } from "../../helpers/screenShare";
import styles from "./BottomBar.module.css"

export const BottomBar = React.memo(({
  onPause,
  onStop,
  sources,
  showLayoutSelector,
  onOpenSources,
  notificationCount,
  sourcesActive,
  onRequestHighlight,
  screenShare,
}: {
  onUpdateMeetingState: (state: MeetingState) => void;
  onPause: () => void;
  onStop: () => void;
  sources: MeetingSourceExtended[];
  showLayoutSelector?: boolean;
  onOpenSources?: () => void;
  notificationCount?: number;
  sourcesActive?: boolean;
  onRequestHighlight?: () => void;
  /**
   * Held by the page: the bar is remounted when the layout switches between
   * mobile and desktop, and on mobile the button sits in a menu that unmounts
   * when it closes. The share must outlive both.
   */
  screenShare?: ScreenShareState;
}) => {
  const { containerAPI } = useMeetingContainerContext();
  const [session] = useMeetingSelector(containerAPI, "session");
  const [audioPermitted] = useMeetingSelector(containerAPI, "session", session => session?.permissions?.audio ?? true);
  const [meetingShortId] = useMeetingSelector(containerAPI, "meeting", meeting => meeting?.shortId);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);

  const popupMode = useAppStoreSelector(state => state.popupMode);

  const dispatch = useAppStoreDispatch();

  // The screen share has its own button: the camera and mic leave it alone.
  const hasBackendMedias = getTogglableMedias(session).length > 0;

  const handleCameraToggle = useCallback(async () => {
    if (!containerAPI) return;
    const newEnabled = !videoEnabled;

    if (session && hasBackendMedias) {
      const manager = containerAPI.emissionManager;
      const managerEnabled = togglableMediasEnabled(manager, session, "video");
      // A ui/manager mismatch means the click does the opposite of the icon.
      diagLogger.log("bottombar camera toggle", { uiEnabled: videoEnabled, managerEnabled, action: managerEnabled ? "disable" : "enable" });
      const selector = getTogglableMediasSelector(session);
      let success = true;
      if (managerEnabled) {
        success = (await containerAPI.disableVideo(selector)).success;
      } else {
        success = (await containerAPI.enableVideo(selector)).success;
      }
      if (!success) toastError("Une erreur est survenue...");
      // A by-id selector only flips the tracks: the manager's flags follow
      // the session update, so read the outcome from the request instead.
      const enabledNow = "sessions" in selector ? (success ? !managerEnabled : managerEnabled) : manager.videoIsEnabled({ mode: "some" });
      diagLogger.log("bottombar camera toggle done", { success, managerEnabled: enabledNow });
      dispatch("setVideoStreamEnabled", enabledNow);
    } else {
      diagLogger.log("bottombar camera toggle", { uiEnabled: videoEnabled, hasBackendMedias: false, to: newEnabled });
      dispatch("setVideoStreamEnabled", newEnabled);
    }
  }, [containerAPI, dispatch, videoEnabled, hasBackendMedias, session]);

  const handleAudioToggle = useCallback(async () => {
    if (!containerAPI) return;
    const newEnabled = !audioEnabled;

    if (session && hasBackendMedias) {
      const manager = containerAPI.emissionManager;
      const managerEnabled = togglableMediasEnabled(manager, session, "audio");
      diagLogger.log("bottombar audio toggle", { uiEnabled: audioEnabled, managerEnabled, action: managerEnabled ? "mute" : "unmute" });
      const selector = getTogglableMediasSelector(session);
      let success = true;
      if (managerEnabled) {
        success = (await containerAPI.mute(selector)).success;
      } else {
        success = (await containerAPI.unmute(selector)).success;
      }
      if (!success) toastError("Une erreur est survenue...");
      const enabledNow = "sessions" in selector ? (success ? !managerEnabled : managerEnabled) : manager.audioIsEnabled({ mode: "some" });
      diagLogger.log("bottombar audio toggle done", { success, managerEnabled: enabledNow });
      dispatch("setAudioStreamEnabled", enabledNow);
    } else {
      diagLogger.log("bottombar audio toggle", { uiEnabled: audioEnabled, hasBackendMedias: false, to: newEnabled });
      dispatch("setAudioStreamEnabled", newEnabled);
    }
  }, [containerAPI, dispatch, audioEnabled, hasBackendMedias, session]);

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);

  const sourcesActive_ = popupMode === "sources";
  const shareActive = popupMode === "share";

  const handleSourcePickerToggle = useCallback(() => {
    dispatch("setPopupMode", sourcesActive_ ? undefined : "sources");
  }, [dispatch, sourcesActive_]);

  const handleShareClick = useCallback(() => {
    dispatch("setPopupMode", shareActive ? undefined : "share");
  }, [dispatch, shareActive]);

  const handleWorkBoxToggle = useCallback(() => {
    dispatch("setWorkBoxOpen", !workBoxOpen);
  }, [dispatch, workBoxOpen]);

  const left = useMemo(() => {
    const items: React.ReactNode[] = [];

    if (isMobile) {
      // Mobile: workbox toggle button
      items.push(
        <PrimaryButton onClick={handleWorkBoxToggle} key="workbox-toggle">
          {CircleIcon.Variants.WorkBox({
            active: workBoxOpen,
            notificationCount: !workBoxOpen ? notificationCount : undefined,
          })}
        </PrimaryButton>
      );
    }

    if (showLayoutSelector) {
      items.push(
        <LayoutSelector
          key="layout-selector"
          className={styles.layoutSelector}
        />
      );
    }

    return items;
  }, [showLayoutSelector, isMobile, handleWorkBoxToggle, workBoxOpen, notificationCount, sourcesActive]);

  const renderCameraButton = useMemo(() => {
    const permitted = !(session?.permissions?.video === false);
    const currRef = createRef<HTMLDivElement>();
    let hoverMessage: string | undefined = undefined;
    if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
    return (
      <div ref={currRef} key="student-dashboard-overlay-camera-btn">
        <PrimaryButton onClick={handleCameraToggle}>
          <CircleIcon.Variants.Camera
            unavailable={!permitted}
            disabled={!videoEnabled}
          />
        </PrimaryButton>
        {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
      </div>
    );
  }, [session?.permissions?.video, videoEnabled, handleCameraToggle]);

  const renderAudioButton = useMemo(() => {
    const currRef = createRef<HTMLDivElement>();
    let hoverMessage: string | undefined = undefined;
    if (!audioPermitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
    return (
      <div ref={currRef} key="student-dashboard-overlay-microphone-btn">
        <PrimaryButton onClick={handleAudioToggle}>
          <CircleIcon.Variants.Mic
            unavailable={!audioPermitted}
            disabled={!audioEnabled}
          />
        </PrimaryButton>
        {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
      </div>
    );
  }, [audioPermitted, audioEnabled, handleAudioToggle]);
  
  const renderPauseButton = useMemo(() => (
    <PrimaryButton onClick={onPause} key="pause-btn">
      <CircleIcon.Variants.Pause />
    </PrimaryButton>
  ), [onPause]);

  const renderHangOffButton = useMemo(() => (
    <PrimaryButton onClick={onStop} key="hangoff-btn">
      <CircleIcon.Variants.HangOff
        style={{
          padding: "0.75em",
        }}
      />
    </PrimaryButton>
  ), [onStop]);

  const renderScreenShareButton = useMemo(() => (
    screenShare?.available
      ? <ScreenShareButton key="screen-share-btn" status={screenShare.status} onToggle={screenShare.toggle} />
      : null
  ), [screenShare?.available, screenShare?.status, screenShare?.toggle]);

  const center = useMemo(() => {
    const items: React.ReactNode[] = [];

    // On mobile, pause/hang-up move to the toolbox menu (see `right`).
    if (isExpert(session) && !isMobile) {
      items.push(renderPauseButton);
      items.push(renderHangOffButton);
    }

    // Show media buttons for non-spectators, or spectators who have backend medias (on the board)
    if (!isSpectator(session) || hasBackendMedias) {
      items.push(renderCameraButton);
      items.push(renderAudioButton);
    }

    // On mobile it moves to the toolbox
    if (renderScreenShareButton && !isMobile) items.push(renderScreenShareButton);

    // Show highlight button for non-experts (spectators included) — on mobile it moves to the toolbox
    if (!isExpert(session) && !isMobile) {
      items.push(<HighlightButton key="highlight-btn" onRequestHighlight={onRequestHighlight} />);
    }

    return items;
  }, [session, hasBackendMedias, isMobile, renderPauseButton, renderHangOffButton, renderCameraButton, renderAudioButton, renderScreenShareButton, onRequestHighlight]);

  const right = useMemo(() => {
    if (isMobile) {
      // Pause, hang-up, share and sources are tucked into a single toolbox menu
      // at the far right to keep the mobile bar uncluttered.
      const tools: React.ReactNode[] = [];
      if (isExpert(session)) {
        tools.push(renderPauseButton);
        tools.push(renderHangOffButton);
      }
      if (meetingShortId) {
        tools.push(
          <PrimaryButton key="share-toggle" onClick={handleShareClick}>
            <SquareIcon.Variants.QRCode containerSize={46} iconSize={22} selected={shareActive} />
          </PrimaryButton>
        );
      }
      if (onOpenSources && sources.some(s => s.visible)) {
        tools.push(
          <PrimaryButton onClick={onOpenSources} key="source-toggle">
            {CircleIcon.Variants.Sources({ active: sourcesActive })}
          </PrimaryButton>
        );
      }
      if (!isExpert(session)) {
        tools.push(<HighlightButton key="highlight-btn" onRequestHighlight={onRequestHighlight} />);
      }
      if (renderScreenShareButton) tools.push(renderScreenShareButton);
      if (!tools.length) return [];
      return [
        <ToolboxMenu
          key="toolbox"
          closeOnSelect
          align="end"
          trigger={({ open, toggle }) => (
            <PrimaryButton onClick={toggle}>
              <CircleIcon.Variants.Toolbox active={open} />
            </PrimaryButton>
          )}
        >
          {tools}
        </ToolboxMenu>,
      ];
    }
    const items: React.ReactNode[] = [<AppSoundSlider key="sound-slider" />];
    if (meetingShortId) {
      items.push(
        <PrimaryButton key="share-toggle" onClick={handleShareClick}>
          <SquareIcon.Variants.QRCode containerSize={50} iconSize={25} selected={shareActive} />
        </PrimaryButton>
      );
    }
    items.push(
      <MediaSourceViewer
        key="media-source-viewer"
        sources={sources}
        onClick={handleSourcePickerToggle}
        selected={sourcesActive_}
      />
    );
    return items;
  }, [sources, handleSourcePickerToggle, sourcesActive_, isMobile, onOpenSources, sourcesActive, meetingShortId, handleShareClick, shareActive, session, renderPauseButton, renderHangOffButton, renderScreenShareButton, onRequestHighlight]);

  const style = useMemo(() => ({
    height: "unset",
    padding: "unset",
    backgroundColor: "transparent",
    boxShadow: "unset",
  }), []);

  if (!session) return null;
  
  return (
    <TopBar 
      style={style}
      left={left}
      center={center}
      right={right}
    />
  );
});